import React, { memo } from 'react';
import { ArrowRight, Star, Check, TrendingUp } from 'lucide-react';
import { handleWhatsAppClick } from '@/lib/whatsapp.js';
import WhatsAppIcon from './WhatsAppIcon.jsx';
import heroProdutos from '@/assets/hero-produtos.webp';
import heroProdutos700 from '@/assets/hero-produtos-700.webp';

const HeroSection = memo(() => {
  return (
    <section id="hero" className="relative w-full max-w-[100vw] bg-hero pt-24 pb-12 lg:pt-32 lg:pb-24 overflow-hidden">
      {/* Decorative ambient background orbs */}
      <div className="absolute -top-24 -left-24 w-72 h-72 md:w-96 md:h-96 bg-primary/20 rounded-full blur-[100px] pointer-events-none z-0"></div>
      <div className="absolute top-1/3 -right-20 w-64 h-64 md:w-80 md:h-80 bg-accent/10 rounded-full blur-[100px] pointer-events-none z-0"></div>

      <div className="container-custom relative z-10 w-full">
        
        {/* Split Layout Container */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center w-full">
          
          {/* LEFT COLUMN: Text Content & CTA */}
          <div className="flex flex-col items-start text-left w-full z-10 order-1 lg:order-1 pt-4 lg:pt-0">
            
            {/* Top Rating & Trust Badges */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-4 md:mb-6 bg-white/5 border border-white/10 px-4 py-2 rounded-lg backdrop-blur-sm shadow-sm max-w-full">
              <div className="flex text-hero-subtitle flex-shrink-0" aria-label="Avaliação 4.9 de 5 estrelas">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
              </div>
              <span className="text-white/90 text-xs md:text-sm font-bold tracking-wide break-words">
                4.9/5 - Mais de 500 Clientes Satisfeitos
              </span>
            </div>

            {/* Main Headline (Unique H1) */}
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-extrabold text-white mb-3 md:mb-5 leading-[1.1] tracking-tight text-balance w-full break-words">
              Pães, Doces e Salgados Congelados de Padrão Industrial.
            </h1>

            {/* Highlight Subtitle with WCAG AA Contrast */}
            <p className="font-body text-xl sm:text-2xl md:text-3xl font-extrabold text-hero-subtitle mb-4 md:mb-6 text-balance drop-shadow-sm w-full break-words">
              Prontos para Vender e Lucrar até 40%
            </p>

            {/* Concise Description */}
            <p className="font-body text-base md:text-lg text-white/80 leading-relaxed mb-6 md:mb-8 max-w-xl text-balance font-medium w-full break-words">
              Produtos com padrão de qualidade impecável, entrega em 24h para todo o Rio Grande do Sul e condições comerciais competitivas. Mantenha sua loja sempre abastecida, sem desperdício e reduza sua mão de obra.
            </p>

            {/* CTA Area (Visible above the fold on mobile when possible) */}
            <div className="flex flex-col items-start w-full max-w-md mb-6 md:mb-8">
              <button 
                data-gtm="whatsapp"
                onClick={() => handleWhatsAppClick('Olá! Gostaria de aumentar minha margem de lucro. Podem me enviar a tabela de produtos congelados?')} 
                className="gtm-whatsapp cta-button cta-button-full btn-primary-cro botao-cta-whats w-full"
              >
                <WhatsAppIcon className="cta-icon flex-shrink-0" />
                <span className="whitespace-normal text-center break-words leading-tight flex-1">RECEBER TABELA NO WHATSAPP</span>
                <ArrowRight className="cta-arrow animate-bounce-right flex-shrink-0" />
              </button>
              <p className="mt-3 text-xs md:text-sm text-white/60 font-medium w-full text-center sm:text-left break-words">
                Resposta rápida garantida por nossos consultores.
              </p>
            </div>

            {/* Trust Checklist */}
            <div className="w-full">
              <div className="hero-trust-list justify-start w-full flex-wrap">
                {["35 Anos de Tradição", "Atendimento VIP", "Direto da Fábrica"].map((item, index) => (
                  <span key={index} className="hero-trust-item flex-shrink-0">
                    <Check className="w-4 h-4 text-hero-subtitle shrink-0" />
                    {item}
                  </span>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN: High-quality Product Image */}
          <div className="w-full relative flex justify-center lg:justify-end order-2 lg:order-2 max-w-full">
            
            {/* Reusable Image Card Container with explicit dimensions to prevent CLS */}
            <div className="img-container-cls border-4 border-white/15 bg-hero w-full">
              {/* Fallback blurred background while loading */}
              <div className="absolute inset-0 bg-secondary/50 animate-pulse"></div>
              
              <img
                src={heroProdutos}
                srcSet={`${heroProdutos700} 700w, ${heroProdutos} 1000w`}
                sizes="(min-width: 1024px) 576px, (min-width: 640px) 592px, calc(100vw - 2.5rem)"
                alt="Pães, doces e salgados congelados de padrão industrial com excelente iluminação e apresentação visual"
                width="1000"
                height="558"
                fetchPriority="high"
                className="img-content-cls relative z-10 transform hover:scale-105 transition-transform duration-700 w-full h-full object-cover"
              />

              {/* Subtle gradient overlay to blend edges if necessary */}
              <div className="absolute inset-0 bg-gradient-to-t from-hero-bg/40 to-transparent pointer-events-none z-20"></div>
            </div>

            {/* Decorative background glow behind the image */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-primary/10 rounded-full blur-[80px] pointer-events-none z-0"></div>

            {/* Floating profit stat card, overlapping the image */}
            <div className="absolute -bottom-5 -left-3 sm:bottom-6 sm:-left-6 z-30 bg-white rounded-2xl shadow-[0_20px_40px_-12px_rgba(0,0,0,0.35)] px-4 py-3 sm:px-5 sm:py-4 flex items-center gap-3 max-w-[calc(100%-1.5rem)]">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="leading-tight">
                <p className="font-heading text-xl sm:text-2xl font-extrabold text-foreground">até 40%</p>
                <p className="text-[11px] sm:text-xs text-muted-foreground font-semibold">de lucro na revenda</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
});

HeroSection.displayName = 'HeroSection';
export default HeroSection;